import React from 'react';
import { useLocale } from '../contexts/LocaleContext';
import { RichTextField } from './RichTextField';
import type { MentionableEntity } from './RichTextField';
import { SingleFileDropZone } from './SingleFileDropZone';
import { Button } from './ui/button';
import { CancelButton } from './ui/cancel-button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogFooter,
  DialogPrimaryShortcut,
} from './ui/dialog';
import { ButtonTooltip, TooltipProvider } from './ui/tooltip';
import { cn } from '@/lib/utils';
import { ProjectPill } from './ProjectPill';
import { RotatingTrashIcon } from './RotatingTrashIcon';
import { SlidingFolderInputIcon } from './SlidingFolderInputIcon';
import {
  ENTITY_DETAIL_DIALOG_CONTENT_CLASSNAME,
  EntityDetailModalLayout,
  EntityDetailModalTitle,
} from './EntityDetailModalLayout';

export interface NoteFormProjectOption {
  id: string;
  title: string;
}

export interface NoteFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  content: string;
  onContentChange: (value: string) => void;
  file: File | null;
  onFileChange: (file: File | null) => void;
  existingAttachmentFileName?: string | null;
  onDownloadExistingAttachment?: () => void;
  mentionableEntities?: MentionableEntity[];
  contentLength: number;
  maxLength: number;
  contentOk: boolean;
  saving: boolean;
  uploadingAttachment?: boolean;
  createdAt?: string;
  onDelete?: () => void;
  onMoveToProject?: () => void;
  projectOptions?: NoteFormProjectOption[];
  selectedProjectId?: string | null;
  onSelectedProjectIdChange?: (projectId: string | null) => void;
  onSave: () => void;
  onCancel: () => void;
  readOnly?: boolean;
  fieldKey?: string;
}

export const NoteFormDialog: React.FC<NoteFormDialogProps> = ({
  open,
  onOpenChange,
  mode,
  content,
  onContentChange,
  file,
  onFileChange,
  existingAttachmentFileName,
  onDownloadExistingAttachment,
  mentionableEntities = [],
  contentLength,
  maxLength,
  contentOk,
  saving,
  uploadingAttachment = false,
  onDelete,
  onMoveToProject,
  projectOptions = [],
  selectedProjectId = null,
  onSelectedProjectIdChange,
  onSave,
  onCancel,
  readOnly = false,
  fieldKey,
}) => {
  const { t } = useLocale();
  const title = readOnly
    ? (t('Notes') || 'Notes')
    : mode === 'create'
      ? (t('create_note') || 'Create note')
      : (t('edit_note') || 'Edit note');
  const busy = saving || uploadingAttachment;
  const showProjectPicker = !readOnly && Boolean(onSelectedProjectIdChange) && projectOptions.length > 0;
  const characterCountLabel = `${contentLength} / ${maxLength}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(ENTITY_DETAIL_DIALOG_CONTENT_CLASSNAME, 'sm:max-w-2xl')}
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <EntityDetailModalLayout
          header={<EntityDetailModalTitle>{title}</EntityDetailModalTitle>}
          footer={
            <DialogFooter className="flex-row flex-wrap items-center justify-between gap-2 sm:flex-nowrap sm:justify-between">
              <TooltipProvider>
                <div className="flex shrink-0 justify-start gap-2">
                  {!readOnly && onDelete && (
                    <ButtonTooltip tooltip={t('move_note_to_trash_tooltip')} side="top">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="group inline-flex h-8 w-8 items-center gap-1.5 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive sm:w-auto sm:px-3"
                        onClick={onDelete}
                        disabled={busy}
                        aria-label={t('move_to_trash')}
                      >
                        <RotatingTrashIcon />
                        <span className="hidden sm:inline">{t('move_to_trash')}</span>
                      </Button>
                    </ButtonTooltip>
                  )}
                  {!readOnly && onMoveToProject && (
                    <ButtonTooltip tooltip={t('move_note_to_project_tooltip')} side="top">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="group inline-flex h-8 w-8 items-center gap-1.5 p-0 sm:w-auto sm:px-3"
                        onClick={onMoveToProject}
                        disabled={busy}
                        aria-label={t('move_to_project')}
                      >
                        <SlidingFolderInputIcon />
                        <span className="hidden sm:inline">{t('move_to_project') || 'Move to project'}</span>
                      </Button>
                    </ButtonTooltip>
                  )}
                </div>
              </TooltipProvider>
              <div className="ml-auto flex gap-2">
                <CancelButton onClick={onCancel}>
                  {readOnly ? (t('close') || 'Close') : (t('cancel') || 'Cancel')}
                </CancelButton>
                {!readOnly && (
                  <Button
                    onClick={onSave}
                    disabled={!contentOk || busy}
                    dialogPrimary
                    className="inline-flex items-center gap-1.5"
                  >
                    {busy ? (
                      t('saving') || 'Saving...'
                    ) : (
                      <>
                        {t('save') || 'Save'}
                        <DialogPrimaryShortcut className="ml-0.5" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            </DialogFooter>
          }
        >
          <div className="flex min-h-0 flex-col gap-4 p-4">
            <RichTextField
              key={fieldKey}
              value={content}
              onChange={onContentChange}
              placeholder={t('note_placeholder') || 'Write your note...'}
              mentionableEntities={mentionableEntities}
              minHeight="120px"
              maxHeight="40vh"
              editable={!readOnly}
              autoFocus={!readOnly}
            />
            {!readOnly && (
              <div className="flex min-w-0 items-center gap-2">
                {showProjectPicker && onSelectedProjectIdChange && (
                  <div className="flex min-w-0 flex-1 flex-nowrap gap-2 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    {projectOptions.map((project) => {
                      const selected = selectedProjectId === project.id;
                      return (
                        <ProjectPill
                          key={project.id}
                          title={project.title}
                          selected={selected}
                          disabled={busy}
                          onToggle={() => {
                            onSelectedProjectIdChange(selected ? null : project.id);
                          }}
                        />
                      );
                    })}
                  </div>
                )}
                <div
                  className="ml-auto shrink-0 text-xs text-muted-foreground"
                  aria-label={characterCountLabel}
                >
                  {characterCountLabel}
                </div>
              </div>
            )}
            {(file || existingAttachmentFileName || !readOnly) && (
              <SingleFileDropZone
                file={file}
                onFileChange={onFileChange}
                existingFileName={existingAttachmentFileName}
                onDownloadExisting={onDownloadExistingAttachment}
                disabled={busy || readOnly}
              />
            )}
          </div>
        </EntityDetailModalLayout>
      </DialogContent>
    </Dialog>
  );
};
